import { Fragment, useMemo } from "react";
import { FieldArray } from "formik";
import { Button, Card, CardBody, CardHeader, Input, Table } from "reactstrap";
import {
  QuotationItemType,
  QuotationItemTypeLabelMap,
} from "features/Quotations/constants/quotation.constants";
import { formatCurrency } from "utils/currency";
import { quotationLineAmount } from "features/Quotations/utils/quotationItem.utils";
import { AsyncPaginatedSelect } from "components/common/AsyncPaginatedSelect";
import { getSupplierOptions } from "features/Suppliers/services/SupplierService";
import {
  SupplierOption,
  SupplierSingleValue,
} from "features/Suppliers/components/SupplierSelectComponents";

export type BoqItemsFieldArrayProps = {
  validation: any;
  /** When true, table is display-only (e.g. petty work not in New status). */
  readOnly?: boolean;
  /**
   * When true, omit the outer Card (and title block) so the BOQ can sit inside a parent
   * form card (e.g. material request, payment-style layout).
   */
  embedded?: boolean;
  cardTitle?: string;
  cardSubtitle?: string;
  addLineLabel?: string;
  /** When true, the Type select is shown as disabled (locked to its current value). */
  typeSelectDisabled?: boolean;
  getDefaultRow?: () => {
    type: QuotationItemType;
    description: string;
    quantity: number;
    unit: string;
    rate: number;
  };
  /** When true, renders a per-line supplier select after the Description column. */
  showSupplierColumn?: boolean;
  /** When true, the supplier select remains interactive even when readOnly=true. */
  supplierColumnEditable?: boolean;
};

const defaultQuotationRow = () => ({
  type: QuotationItemType.SERVICE,
  description: "",
  quantity: 1,
  unit: "",
  rate: 0,
});

function BoqTableHead({
  includeActionColumn,
  showSupplierColumn,
}: {
  includeActionColumn: boolean;
  showSupplierColumn?: boolean;
}) {
  return (
    <thead>
      <tr className="table-active">
        <th scope="col" style={{ width: "40px" }}>
          #
        </th>
        <th scope="col" style={{ minWidth: "120px" }}>
          Type
        </th>
        <th scope="col" style={{ minWidth: "220px" }}>
          Description
        </th>
        {showSupplierColumn ? (
          <th scope="col" style={{ minWidth: "180px", maxWidth: "200px" }}>
            Supplier
          </th>
        ) : null}
        <th scope="col" style={{ minWidth: "88px" }}>
          Unit
        </th>
        <th scope="col" style={{ minWidth: "100px" }}>
          Qty
        </th>
        <th scope="col" style={{ minWidth: "100px" }}>
          Rate
        </th>
        <th scope="col" className="text-end" style={{ minWidth: "100px" }}>
          Amount
        </th>
        {includeActionColumn ? (
          <th scope="col" className="text-end" style={{ width: "100px" }}>
            Action
          </th>
        ) : null}
      </tr>
    </thead>
  );
}

/**
 * Shared bill-of-quantities line editor (material / labour / service).
 * Used by quotation, petty work, and AMC forms; line `type` values match `QuotationItemType`.
 */
export default function BoqItemsFieldArray({
  validation,
  readOnly = false,
  embedded = false,
  cardTitle = "Bill of quantities (BOQ)",
  cardSubtitle = "Materials, labour, and services — each line stores description, unit, quantity, and rate (amount is quantity × rate).",
  addLineLabel = "Add line",
  typeSelectDisabled = false,
  getDefaultRow,
  showSupplierColumn = false,
  supplierColumnEditable = false,
}: BoqItemsFieldArrayProps) {
  const newRow = getDefaultRow ?? defaultQuotationRow;
  const items = useMemo(
    () => validation.values.items ?? [],
    [validation.values.items],
  );
  /** Small positive minimum for quantity steppers. */
  const minQty = 0.01;
  const total = useMemo(
    () =>
      items.reduce(
        (sum: number, item: any) =>
          sum +
          quotationLineAmount({
            quantity: Number(item.quantity) || 0,
            rate: Number(item.rate) || 0,
          }),
        0,
      ),
    [items],
  );
  const totalColSpan = showSupplierColumn ? 7 : 6;

  if (readOnly) {
    const readOnlyTable = (
      <div className="table-card" style={{ overflowX: "auto" }}>
        <Table className="table-borderless table-nowrap align-middle mb-0">
          <BoqTableHead
            includeActionColumn={false}
            showSupplierColumn={showSupplierColumn}
          />
          <tbody>
            {items.length === 0 ? (
              <tr>
                <td colSpan={7} className="text-center text-muted py-4">
                  No line items.
                </td>
              </tr>
            ) : (
              items.map((item: any, index: number) => {
                const qty = Number(item.quantity) || 0;
                const rate = Number(item.rate) || 0;
                const amount = quotationLineAmount({ quantity: qty, rate });
                const rowType =
                  (item.type as QuotationItemType) ??
                  QuotationItemType.MATERIAL;
                const typeLabel =
                  QuotationItemTypeLabelMap[rowType] ?? String(item.type);
                return (
                  <tr key={index}>
                    <th scope="row">{index + 1}</th>
                    <td className="text-muted small">{typeLabel}</td>
                    <td
                      className="text-muted"
                      style={{ whiteSpace: "pre-wrap" }}
                    >
                      {String(item.description ?? "").trim() || "—"}
                    </td>
                    {showSupplierColumn ? (
                      supplierColumnEditable ? (
                        <td
                          style={{
                            width: "200px",
                            minWidth: "160px",
                            maxWidth: "200px",
                          }}
                        >
                          <div style={{ width: "100%", maxWidth: "200px" }}>
                            <AsyncPaginatedSelect
                              fetchOptions={getSupplierOptions}
                              placeholder="Supplier (optional)"
                              value={item.supplierId ?? null}
                              components={{
                                Option: SupplierOption,
                                SingleValue: SupplierSingleValue,
                              }}
                              onChange={(opt: any) =>
                                validation.setFieldValue(
                                  `items[${index}].supplierId`,
                                  opt?.value ?? null,
                                )
                              }
                              onBlur={() =>
                                validation.setFieldTouched(
                                  `items[${index}].supplierId`,
                                  true,
                                )
                              }
                              menuPlacement="auto"
                            />
                          </div>
                        </td>
                      ) : (
                        <td className="text-muted small">
                          {item.supplier?.name?.trim() ||
                            (item.supplierId
                              ? `Supplier #${item.supplierId}`
                              : "—")}
                        </td>
                      )
                    ) : null}
                    <td className="text-muted tabular-nums">
                      {String(item.unit ?? "").trim() || "—"}
                    </td>
                    <td className="tabular-nums">{qty || "—"}</td>
                    <td className="tabular-nums">
                      {item.rate != null && item.rate !== "" ? item.rate : "—"}
                    </td>
                    <td className="text-end text-body fw-medium tabular-nums">
                      {formatCurrency(amount)}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
          {items.length > 0 ? (
            <tfoot>
              <tr className="table-light">
                <th colSpan={totalColSpan} className="text-end align-middle">
                  <span className="text-muted fw-normal me-2">
                    {items.length} line item{items.length === 1 ? "" : "s"}
                  </span>
                  Total
                </th>
                <th className="text-end align-middle fs-15 fw-bold text-primary tabular-nums">
                  {formatCurrency(total)}
                </th>
              </tr>
            </tfoot>
          ) : null}
        </Table>
      </div>
    );

    if (embedded) {
      return readOnlyTable;
    }

    return (
      <Card>
        <CardHeader className="d-flex align-items-center justify-content-between gap-2">
          <div>
            <h5 className="card-title mb-1">{cardTitle}</h5>
            <p className="text-muted mb-0 small">{cardSubtitle}</p>
          </div>
        </CardHeader>
        <CardBody className="p-4">{readOnlyTable}</CardBody>
      </Card>
    );
  }

  const editableFieldArray = (
    <FieldArray name="items">
      {({ push, remove }) => (
        <>
          <div className="table-card" style={{ overflowX: "auto" }}>
            <Table className="table-borderless table-nowrap mb-0 ">
              <BoqTableHead
                includeActionColumn
                showSupplierColumn={showSupplierColumn}
              />
              <tbody>
                {items.map((item: any, index: number) => {
                  const qty = Number(item.quantity) || 0;
                  const rate = Number(item.rate) || 0;
                  const previewAmount = quotationLineAmount({
                    quantity: qty,
                    rate,
                  });
                  return (
                    <tr key={index}>
                      <th scope="row">{index + 1}</th>
                      <td>
                        <Input
                          type="select"
                          name={`items[${index}].type`}
                          value={item.type}
                          disabled={typeSelectDisabled}
                          onChange={(e) => {
                            const next = e.target.value as QuotationItemType;
                            validation.setFieldValue(
                              `items[${index}].type`,
                              next,
                            );
                            if (next !== QuotationItemType.MATERIAL) {
                              validation.setFieldValue(
                                `items[${index}].lineId`,
                                undefined,
                              );
                            }
                            validation.setFieldTouched(
                              `items[${index}].type`,
                              true,
                            );
                          }}
                          onBlur={validation.handleBlur}
                          invalid={
                            !!(
                              validation.touched.items?.[index]?.type &&
                              validation.errors.items?.[index]?.type
                            )
                          }
                        >
                          {Object.values(QuotationItemType).map((t) => (
                            <option key={t} value={t}>
                              {QuotationItemTypeLabelMap[t]}
                            </option>
                          ))}
                        </Input>
                      </td>
                      <td className="align-top">
                        <Input
                          type="textarea"
                          rows={2}
                          name={`items[${index}].description`}
                          placeholder="Line description"
                          value={item.description}
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          invalid={
                            !!(
                              (validation.touched.items?.[index]?.description ||
                                validation.submitCount > 0) &&
                              validation.errors.items?.[index]?.description
                            )
                          }
                        />
                        {(validation.touched.items?.[index]?.description ||
                          validation.submitCount > 0) &&
                          validation.errors.items?.[index]?.description && (
                            <div className="invalid-feedback d-block">
                              {validation.errors.items?.[index]?.description}
                            </div>
                          )}
                      </td>
                      {showSupplierColumn ? (
                        <td
                          style={{
                            width: "200px",
                            minWidth: "160px",
                            maxWidth: "200px",
                          }}
                        >
                          <div style={{ width: "100%", maxWidth: "200px" }}>
                            <AsyncPaginatedSelect
                              fetchOptions={getSupplierOptions}
                              placeholder="Supplier (optional)"
                              value={item.supplierId ?? null}
                              components={{
                                Option: SupplierOption,
                                SingleValue: SupplierSingleValue,
                              }}
                              onChange={(opt: any) =>
                                validation.setFieldValue(
                                  `items[${index}].supplierId`,
                                  opt?.value ?? null,
                                )
                              }
                              onBlur={() =>
                                validation.setFieldTouched(
                                  `items[${index}].supplierId`,
                                  true,
                                )
                              }
                              menuPlacement="auto"
                            />
                          </div>
                        </td>
                      ) : null}
                      <td>
                        <Input
                          type="text"
                          name={`items[${index}].unit`}
                          placeholder="e.g. day, sqft"
                          value={item.unit}
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          invalid={
                            !!(
                              validation.touched.items?.[index]?.unit &&
                              validation.errors.items?.[index]?.unit
                            )
                          }
                        />
                      </td>
                      <td>
                        <div className="input-step">
                          <button
                            type="button"
                            className="minus"
                            onClick={() =>
                              validation.setFieldValue(
                                `items[${index}].quantity`,
                                Math.max(minQty, Number(item.quantity) - 1),
                              )
                            }
                          >
                            -
                          </button>
                          <Input
                            type="number"
                            min={minQty}
                            step="any"
                            name={`items[${index}].quantity`}
                            value={item.quantity}
                            onBlur={validation.handleBlur}
                            onChange={(e) => {
                              const n = Number(e.target.value);
                              validation.setFieldValue(
                                `items[${index}].quantity`,
                                Number.isFinite(n) && n >= minQty ? n : minQty,
                              );
                            }}
                            invalid={
                              !!(
                                validation.touched.items?.[index]?.quantity &&
                                validation.errors.items?.[index]?.quantity
                              )
                            }
                          />
                          <button
                            type="button"
                            className="plus"
                            onClick={() =>
                              validation.setFieldValue(
                                `items[${index}].quantity`,
                                Number(item.quantity) + 1,
                              )
                            }
                          >
                            +
                          </button>
                        </div>
                      </td>
                      <td>
                        <Input
                          type="number"
                          min={0}
                          step="any"
                          name={`items[${index}].rate`}
                          value={item.rate}
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          invalid={
                            !!(
                              validation.touched.items?.[index]?.rate &&
                              validation.errors.items?.[index]?.rate
                            )
                          }
                        />
                      </td>
                      <td className="text-end text-body fw-medium">
                        {formatCurrency(previewAmount)}
                      </td>
                      <td className="text-end">
                        <Button
                          color="soft-danger"
                          size="sm"
                          onClick={() => remove(index)}
                          disabled={validation.values.items.length === 1}
                        >
                          <i className="ri-delete-bin-line me-1" />
                          Remove
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="table-light">
                  <th colSpan={totalColSpan} className="align-middle">
                    <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                      <Button
                        type="button"
                        size="sm"
                        onClick={() => push(newRow())}
                        className="btn btn-soft-secondary fw-medium"
                      >
                        <i className="ri-add-fill me-1" />
                        {addLineLabel}
                      </Button>
                      {items.length > 0 ? (
                        <span className="text-muted">
                          {items.length} line item{items.length === 1 ? "" : "s"} ·{" "}
                          <span className="fw-semibold text-body">Total</span>
                        </span>
                      ) : null}
                    </div>
                  </th>
                  <th className="text-end align-middle fs-15 fw-bold text-primary tabular-nums">
                    {items.length > 0 ? formatCurrency(total) : null}
                  </th>
                  <th />
                </tr>
              </tfoot>
            </Table>
          </div>

          {validation.submitCount > 0 &&
            typeof validation.errors.items === "string" && (
              <div className="text-danger small mt-2">
                <i className="ri-error-warning-line me-1" />
                {validation.errors.items}
              </div>
            )}
        </>
      )}
    </FieldArray>
  );

  if (embedded) {
    return (
      <div className="boq-items-field-array-embedded">{editableFieldArray}</div>
    );
  }

  return (
    <Fragment>
      <Card>
        <CardHeader className="d-flex align-items-center justify-content-between gap-2">
          <div>
            <h5 className="card-title mb-1">{cardTitle}</h5>
            <p className="text-muted mb-0 small">{cardSubtitle}</p>
          </div>
        </CardHeader>
        <CardBody className="p-4">{editableFieldArray}</CardBody>
      </Card>
    </Fragment>
  );
}
